/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import {
  Card,
  Button,
  Modal,
  Spinner,
  Collapse,
  Row,
  Col,
  Form,
  OverlayTrigger,
  Popover,
  PopoverBody,
} from 'react-bootstrap';
import type {
  Question,
  QuestionnaireTemplate,
  QuestionSection,
  ScoringRule,
} from '../../types/types';
import QuestionCard from './QuestionCard';
import { getLocalizedString } from '../../util/getLocalizedString';
// import { v4 as uuidv4 } from 'uuid';
import { supabase } from '../../lib/supabase';
// import { InlineEditText } from '../common/InlineTextEdit';
import { useSectionMaxScore } from '../../hooks/useSectionMaxScore';
import { captureSupabaseError } from '../../lib/sentry';

import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { htmlToPreviewText } from '../../util/util';

interface Props {
  section: QuestionSection;
  template: QuestionnaireTemplate;
  updateSection: (section: QuestionSection) => void;
  deleteSection: (sectionId: string) => void;
  editSection: (section: QuestionSection) => void;
  onEditQuestion: (question: Question) => void;
  scoringRules: ScoringRule[];
  onReorder: (newOrder: number, sectionId: string) => void;
  onAddQuestion: () => void;
  saving?: boolean;
}

const SectionBlock: React.FC<Props> = ({
  section,
  template,
  updateSection,
  deleteSection,
  editSection,
  onEditQuestion,
  onReorder,
  saving,
  onAddQuestion,
}) => {
  const [confirmingQuestionId, setConfirmingQuestionId] = useState<
    string | null
  >(null);
  const [deleting, setDeleting] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(true); // 🔹 Default collapsed
  const [showDescModal, setShowDescModal] = useState(false);
  const [draftDescription, setDraftDescription] = useState(
    getLocalizedString(section.description) ?? ''
  );

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        codeBlock: false,
        blockquote: false,
        horizontalRule: false,
        orderedList: false,
      }),
    ],
    content: draftDescription,
  });

  // Keep editor content in sync when opening modal
  useEffect(() => {
    if (editor && showDescModal) {
      editor.commands.setContent(draftDescription || '');
    }
  }, [showDescModal]);

  const toggleCollapse = () => setIsCollapsed(!isCollapsed);

  const {
    maxScore,
    loading: maxScoreLoading,
    refresh: refreshMaxScore,
  } = useSectionMaxScore(section.id);

  // const addQuestion = () => {
  //   const newQuestion: Question = {
  //     id: uuidv4(),
  //     key: `q-${section.key}-${section.questions.length}`,
  //     ordinal: section.questions.length,
  //     type: 'text',
  //     help_text: {},
  //     required: false,
  //     hidden: false,
  //     options: [],
  //     section_id: section.id,
  //   };
  //   updateSection({
  //     ...section,
  //     questions: [...section.questions, newQuestion],
  //   });
  // };

  useEffect(() => {
    refreshMaxScore();
  }, [section]);

  const updateQuestion = (updatedQuestion: Question) => {
    updateSection({
      ...section,
      questions: section.questions.map((q) =>
        q.id === updatedQuestion.id ? updatedQuestion : q
      ),
    });
  };

  const handleReorder = (newOrder: number, questionId: string) => {
    const questionIndex = section.questions.findIndex(
      (q) => q.id === questionId
    );

    if (questionIndex === -1) {
      console.warn('Question not found:', questionId);
      return;
    }

    const reorderedQuestions = [...section.questions].sort(
      (a, b) => (a.ordinal ?? 0) - (b.ordinal ?? 0)
    );
    const [movedQuestion] = reorderedQuestions.splice(questionIndex, 1);
    reorderedQuestions.splice(newOrder, 0, movedQuestion);

    // Update ordinals to match new positions
    const updatedQuestions = reorderedQuestions.map((q, index) => ({
      ...q,
      ordinal: index,
    }));
    updateSection({
      ...section,
      questions: updatedQuestions,
    });
  };

  const handleDeleteQuestion = async (questionId: string) => {
    try {
      setDeleting(true);

      // 🗑️ Delete from Supabase
      const { data, error, status } = await supabase
        .from('questions')
        .update({ deleted: true })
        .eq('id', questionId)
        .select('id');

      console.debug('DELETE /questions result', { status, data, error });

      if (error) throw error;

      // 🧹 Update local state
      updateSection({
        ...section,
        questions: section.questions.filter((q) => q.id !== questionId),
      });
    } catch (err) {
      captureSupabaseError(err, { op: 'question.delete' });
      console.error('Failed to delete question:', err);
      alert('Failed to delete question. Check console for details.');
    } finally {
      setDeleting(false);
      setConfirmingQuestionId(null);
    }
  };

  return (
    <>
      <Card className="mb-3 shadow-sm" data-testid={section.id}>
        <Card.Header
          className="d-flex justify-content-between align-items-center"
          style={{ cursor: 'pointer' }}
          onClick={toggleCollapse}
        >
          <div>
            <h5 className="mb-0 d-flex align-items-center">
              <i
                className={`bi me-2 ${
                  isCollapsed ? 'bi-caret-right-fill' : 'bi-caret-down-fill'
                }`}
              ></i>
              {getLocalizedString(section.title)} ({section.questions.length})
            </h5>
            {/* {section.description && (
              <small className="text-muted">
                {getLocalizedString(section.description)}
              </small>
            )} */}
          </div>
          <div
            className="d-flex align-items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="outline-secondary"
              size="sm"
              className="editor-icon-button"
              disabled={section.ordinal === 0 || saving}
              onClick={() => {
                onReorder(section.ordinal! - 1, section.id);
              }}
            >
              <i className="bi bi-arrow-up" />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="editor-icon-button"
              disabled={
                (template.sections &&
                  section.ordinal >= template.sections.length - 1) ||
                saving
              }
              onClick={() => {
                onReorder(section.ordinal! + 1, section.id);
              }}
            >
              <i className="bi bi-arrow-down" />
            </Button>
            <Button
              variant="outline-secondary"
              size="sm"
              className="editor-icon-button"
              onClick={() => editSection(section)}
            >
              <i className="bi bi-pencil"></i>
            </Button>
            <Button
              variant="outline-danger"
              size="sm"
              className="editor-icon-button"
              onClick={() => deleteSection(section.id)}
            >
              <i className="bi bi-trash"></i>
            </Button>
            <Button
              variant="link"
              size="sm"
              className="editor-icon-button"
              onClick={toggleCollapse}
            >
              {isCollapsed ? (
                <>
                  <i className="bi bi-chevron-down me-1"></i> Expand
                </>
              ) : (
                <>
                  <i className="bi bi-chevron-up me-1"></i> Collapse
                </>
              )}
            </Button>
          </div>
        </Card.Header>

        {/* 🔽 Collapsible content */}
        <Collapse in={!isCollapsed}>
          <div>
            <Card.Body>
              <Row>
                <Col>
                  <div className="mb-3">
                    <div className="d-flex align-items-center">
                      <h5 className="mb-0">Description</h5>
                      <Button
                        variant="link"
                        size="sm"
                        className="ms-2"
                        onClick={() => {
                          setDraftDescription(
                            getLocalizedString(section.description) ?? ''
                          );
                          setShowDescModal(true);
                        }}
                      >
                        <i className="bi bi-pencil"></i> Edit
                      </Button>
                    </div>

                    <i className="text-muted">
                      {htmlToPreviewText(
                        getLocalizedString(section.description) ?? ''
                      )}
                    </i>
                  </div>
                  <div className="d-flex">
                    <Form.Check
                      type="switch"
                      id={`exclude-from-score-${section.id}`}
                      label="Exclude from Score Calculation"
                      checked={!!section.exclude_from_score}
                      onChange={(e) =>
                        updateSection({
                          ...section,
                          exclude_from_score: e.target.checked,
                        })
                      }
                      className="me-3"
                    />
                    <div>
                      <OverlayTrigger
                        trigger="hover"
                        rootClose
                        placement="right"
                        overlay={
                          <Popover id={`popover-exclude-${section.id}`}>
                            <PopoverBody>
                              When enabled, questions in this section will not
                              be included in the overall assessment score
                              calculation. Use this for informational purposes
                              only.
                            </PopoverBody>
                          </Popover>
                        }
                      >
                        <i
                          className="bi bi-info-circle-fill text-muted ms-1"
                          style={{ cursor: 'pointer' }}
                        ></i>
                      </OverlayTrigger>
                    </div>
                  </div>
                  <p>
                    Max score:{' '}
                    <strong>{maxScoreLoading ? '...' : maxScore ?? ''}</strong>
                  </p>
                </Col>
              </Row>

              {section.questions
                .slice()
                .sort((a, b) => {
                  return (a.ordinal ?? 0) - (b.ordinal ?? 0);
                })
                .map((q) => (
                  <QuestionCard
                    key={q.id}
                    question={q}
                    section={section}
                    onEdit={() => onEditQuestion(q)}
                    onDelete={() => setConfirmingQuestionId(q.id)}
                    onSave={updateQuestion}
                    onReorder={handleReorder}
                  />
                ))}

              <Button
                variant="outline-primary"
                size="sm"
                className="mt-2"
                onClick={onAddQuestion}
              >
                <i className="bi bi-plus me-1"></i> Add Question
              </Button>
            </Card.Body>
          </div>
        </Collapse>
      </Card>

      {/* 🔒 Delete confirmation modal */}
      <Modal
        show={!!confirmingQuestionId}
        onHide={() => setConfirmingQuestionId(null)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Delete Question</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this question? This action cannot be
          undone.
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmingQuestionId(null)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() =>
              confirmingQuestionId && handleDeleteQuestion(confirmingQuestionId)
            }
            disabled={deleting}
          >
            {deleting ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={showDescModal}
        onHide={() => setShowDescModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Edit Section Description</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* Toolbar */}
          <div className="d-flex gap-2 mb-2">
            <Button
              size="sm"
              variant={
                editor?.isActive('header') ? 'primary' : 'outline-secondary'
              }
              className="editor-icon-button"
              onClick={() =>
                editor?.chain().focus().toggleHeading({ level: 6 }).run()
              }
            >
              <i className="bi bi-type-h6" />
            </Button>

            <Button
              size="sm"
              variant={
                editor?.isActive('bold') ? 'primary' : 'outline-secondary'
              }
              className="editor-icon-button"
              onClick={() => editor?.chain().focus().toggleBold().run()}
            >
              <i className="bi bi-type-bold" />
            </Button>

            <Button
              size="sm"
              variant={
                editor?.isActive('bulletList') ? 'primary' : 'outline-secondary'
              }
              className="editor-icon-button"
              onClick={() => editor?.chain().focus().toggleBulletList().run()}
            >
              <i className="bi bi-list-ul" />
            </Button>
          </div>

          {/* Editor */}
          <EditorContent
            editor={editor}
            className="border rounded p-2"
            style={{ minHeight: '200px' }}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="primary"
            onClick={() => {
              const html = editor?.getHTML() ?? '';

              updateSection({
                ...section,
                description: html as any,
              });

              setDraftDescription(html);
              setShowDescModal(false);
            }}
          >
            Save
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SectionBlock;
